class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.requestId = 0;
    this.requestController = null;
    this.activeOptionIndex = -1;
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.debouncedOnChange = FoxTheme.utils.debounce(this.onChange.bind(this), 300);
    this.input.addEventListener('input', this.onInput.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('keydown', this.onKeydown.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';

    const isTemplateSearch = this.closest('.template-search');
    if (isTemplateSearch) {
      this.handleClickOutsideBound = this.handleClickOutside.bind(this);
      document.addEventListener('click', this.handleClickOutsideBound);
    }
  }

  disconnectedCallback() {
    this.requestId += 1;
    this.cancelPendingRequest();
    if (this.handleClickOutsideBound) {
      document.removeEventListener('click', this.handleClickOutsideBound);
    }
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get resultOptions() {
    return Array.from(this.querySelectorAll('[role="option"]'));
  }

  onFocus() {
    if (this.closest('.template-search')) {
      document.body.classList.add('predictive-search-open');
      if (this.getQuery().length === 0) {
        this.setExpanded(false);
        return;
      }
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  onInput() {
    this.requestId += 1;
    this.cancelPendingRequest();
    this.finishLoadingState();
    this.resetActiveOption();

    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }

    if (this.closest('.template-search')) {
      document.body.classList.add('predictive-search-open');
    }

    this.debouncedOnChange();
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event && event.preventDefault();
    this.requestId += 1;
    this.cancelPendingRequest();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('loading');
    this.removeAttribute('error');
    this.removeAttribute('results');
    this.resetButton.classList.remove('btn--loading');
    this.resetActiveOption();
    this.setExpanded(false);

    if (this.searchContent) {
      this.searchContent.classList.toggle('hidden', this.searchRecommendationEmpty);
    }
  }

  setupURL() {
    const url = new URL(`${window.location.origin || FoxTheme.routes.shop_url}${FoxTheme.routes.predictive_search_url}`);
    return (
      url.searchParams.set('q', this.getQuery()),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cancelPendingRequest();
    const requestId = ++this.requestId;
    this.removeAttribute('error');

    if (this.cachedMap.has(url)) {
      this.renderSectionFromCache(url, requestId);
      return;
    }

    const controller = new AbortController();
    this.requestController = controller;
    this.renderSectionFromFetch(url, requestId, controller);
  }

  renderSectionFromCache(url, requestId) {
    if (requestId !== this.requestId) return;

    const responseText = this.cachedMap.get(url);
    if (this.renderSearchResults(responseText)) {
      this.setAttribute('results', 'true');
      this.setExpanded(true);
    }
    this.finishLoadingState();
  }

  async renderSectionFromFetch(url, requestId, controller) {
    this.setAttribute('loading', 'true');
    this.resetButton.classList.add('btn--loading');

    try {
      const response = await fetch(url, { signal: controller.signal });
      const contentType = response.headers.get('content-type') || '';

      if (!response.ok || !contentType.includes('text/html')) {
        throw new Error(`Predictive search request failed with status ${response.status}`);
      }

      const responseText = await response.text();
      if (requestId !== this.requestId) return;

      if (!this.renderSearchResults(responseText)) {
        throw new Error('Predictive search response did not contain the expected section');
      }

      this.cachedMap.set(url, responseText);
      this.removeAttribute('error');
      this.setAttribute('results', 'true');
      this.setExpanded(true);
    } catch (error) {
      if (error.name === 'AbortError' || requestId !== this.requestId) return;

      console.error('Error fetching predictive search data: ', error);
      this.setAttribute('error', 'Failed to load data');
      this.removeAttribute('results');
      this.resetActiveOption();
      this.setExpanded(false);

      if (this.searchContent) {
        this.searchContent.classList.toggle('hidden', this.searchRecommendationEmpty);
      }
    } finally {
      if (requestId === this.requestId) {
        this.finishLoadingState();
        if (this.requestController === controller) {
          this.requestController = null;
        }
      }
    }
  }

  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = this.querySelector(`#${CSS.escape(id)}`);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;
        this.resetActiveOption();
        return true;
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }

    return false;
  }

  cancelPendingRequest() {
    if (this.requestController) {
      this.requestController.abort();
      this.requestController = null;
    }
  }

  finishLoadingState() {
    this.removeAttribute('loading');
    this.resetButton.classList.remove('btn--loading');
  }

  setExpanded(expanded) {
    this.input.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  resetActiveOption() {
    this.activeOptionIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
    this.resultOptions.forEach((option) => option.setAttribute('aria-selected', 'false'));
  }

  setActiveOption(index) {
    const options = this.resultOptions;
    if (!options.length) return;

    this.activeOptionIndex = index;
    options.forEach((option, optionIndex) => {
      option.setAttribute('aria-selected', optionIndex === index ? 'true' : 'false');
    });

    const activeOption = options[index];
    this.input.setAttribute('aria-activedescendant', activeOption.id);
    activeOption.scrollIntoView({ block: 'nearest' });
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.resetActiveOption();
      this.setExpanded(false);
      document.body.classList.remove('predictive-search-open');
      return;
    }

    if (!this.hasAttribute('results')) return;

    const options = this.resultOptions;
    if (!options.length) return;

    if (event.key === 'Enter' && this.activeOptionIndex >= 0) {
      event.preventDefault();
      options[this.activeOptionIndex].querySelector('a[href]')?.click();
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    event.preventDefault();
    const direction = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex =
      this.activeOptionIndex < 0
        ? direction > 0
          ? 0
          : options.length - 1
        : (this.activeOptionIndex + direction + options.length) % options.length;

    this.setActiveOption(nextIndex);
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      setTimeout(() => {
        document.body.classList.remove('predictive-search-open');
        this.resetActiveOption();
        this.setExpanded(false);
      });
    }
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class SearchDrawer extends DrawerComponent {
  constructor() {
    super();
  }
  get requiresBodyAppended() {
    return false;
  }
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define('search-drawer', SearchDrawer);
